import { Box, Center, Collapse, Flex, HStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import DevModeDisplayJSON from 'pages/Settings/components/DevModeDisplayJSON';
import React from 'react';
import { SidePanelParentPetDataFragment } from 'shared/types/graphql';
import { useCareActivityLog } from '../../hooks/useCareActivityLog';
import { useCareNote } from '../../hooks/useCareNote';
import ActivityLogCard from './ActivityLogCard';
import ActivityLogFilterPopover from './ActivityLogFilterPopover';

interface CareActivityLogProps {
  selectedPet?: SidePanelParentPetDataFragment;
  organizationPetParentId?: string;
  enrollmentId?: string;
}

export const CareActivityLog = ({
  selectedPet,
  organizationPetParentId,
  enrollmentId,
}: CareActivityLogProps): JSX.Element => {
  const {
    items,
    loadingItems,
    fetchMore,
    canShowMore,
    filteredItems,
    selectedFilters,
    isAllFiltersSelected,
    handleFilterChange,
    onSelectAllFilters,
    onClearAllFilters,
  } = useCareActivityLog({
    organizationPetId: selectedPet?.organizationPet?.id,
    organizationPetParentId,
    enrollmentId,
  });

  const {
    handleCancel,
    handleEdit,
    handleChange,
    handleSave,
    handleDelete: handleDeleteNote,
    isNoteEditorOpen,
    note,
    isSaving,
  } = useCareNote({
    enrollmentId: enrollmentId || '',
  });

  return (
    <Box bgColor="background.subtle" p={4}>
      <Flex alignItems="center" justifyContent="space-between">
        <Heading size="md">Activity</Heading>
        <Flex>
          <Tooltip label={'Add Note'} placement="bottom" openDelay={500}>
            <Button
              isDisabled={!enrollmentId}
              iconName="penSquare"
              onClick={handleEdit}
              size="md"
              variant="ghostNeutral"
            />
          </Tooltip>
          <ActivityLogFilterPopover
            selectedFilters={selectedFilters}
            isAllFiltersSelected={isAllFiltersSelected}
            handleFilterChange={handleFilterChange}
            onSelectAllFilters={onSelectAllFilters}
            onClearAllFilters={onClearAllFilters}
          />
        </Flex>
      </Flex>
      <Collapse in={isNoteEditorOpen}>
        <Textarea
          spellCheck={true}
          size="sm"
          placeholder={'Add an internal note'}
          value={note}
          data-testid="care-log-note-editor-textarea"
          onChange={handleChange}
          maxLength={350}
        />
        <Flex justifyContent="flex-end">
          <Text as="p" size="xs" variant="subtle" mt={1}>
            {note.length} / 350
          </Text>
        </Flex>

        <HStack my={3}>
          <Button onClick={handleCancel} size="sm" variant="tertiary" ml="auto" width="100%">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!note} isLoading={isSaving} size="sm" width="100%">
            Save
          </Button>
        </HStack>
      </Collapse>

      {!!filteredItems?.length ? (
        filteredItems.map((item, index) => (
          <React.Fragment key={`log-item-${item.data.id}`}>
            <DevModeDisplayJSON key={`log-item-${index}-dev`} src={{ __typename: 'ActivityLogItem', ...item }} />
            <ActivityLogCard key={index} item={item} handleDeleteNote={handleDeleteNote} />
          </React.Fragment>
        ))
      ) : (
        <Text as="p" size="sm" w="100%" variant="subtle" textAlign="center" mt={3}>
          There is no activity for this patient.
        </Text>
      )}
      {!!items.length && canShowMore && (
        <Center mt={3}>
          <Button
            variant="ghost"
            size="xs"
            mb={2}
            isLoading={loadingItems}
            onClick={async (): Promise<void> => {
              await fetchMore();
            }}
          >
            Show More
          </Button>
        </Center>
      )}
    </Box>
  );
};
